import { useQuery } from "@tanstack/react-query";
import { Background, BackgroundVariant, ReactFlow, ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listAgentsOptions } from "@/api";
import { buildParentMapFromConnectors, buildTreeEdges, computeTreeLayout, type StepNode } from "./flowEditorModel";
import { StepNodeView } from "./StepNodeView";

export interface DraftFlowStep {
  agentUuid?: string | null;
  instructions?: string | null;
  requiresReview?: boolean;
}

export interface DraftFlowConnector {
  fromStepOrder?: number | null;
  toStepOrder: number;
}

const nodeTypes = { step: StepNodeView };
const LABEL_CHARS = 60;

/** A workflow draft's steps as the Steps editor lays them out, read-only (nothing to drag, add or delete). */
export function FlowDraftCanvas({
  steps,
  connectors,
}: Readonly<{ steps: readonly DraftFlowStep[]; connectors: readonly DraftFlowConnector[] }>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "workflow" });
  const agents = useQuery({ ...listAgentsOptions(), select: (r) => r.data });
  const { nodes, edges } = useMemo(() => {
    // A draft names steps by their order; the editor's helpers take ids, so the order is the id.
    const idOf = (order: number) => `draft-${order}`;
    const base: StepNode[] = steps.map((step, order) => ({
      id: idOf(order),
      type: "step",
      position: { x: 0, y: 0 },
      data: {
        label: (step.instructions ?? "").slice(0, LABEL_CHARS) || t("stepN", { n: order + 1 }),
        agentName: agents.data?.find((a) => a.uuid === step.agentUuid)?.name,
        requiresReview: Boolean(step.requiresReview),
        readOnly: true,
      },
    }));
    const parentMap = buildParentMapFromConnectors(
      base,
      connectors.map((c) => ({ fromStepId: c.fromStepOrder ?? undefined, toStepId: c.toStepOrder })),
      new Map(steps.map((_, order) => [order, idOf(order)])),
    );
    const layout = computeTreeLayout(base, parentMap);
    return {
      nodes: base.map((node) => ({ ...node, position: layout.get(node.id) ?? node.position })),
      // A still picture: no "flowing" edges (and nothing for reduced-motion users to stop).
      edges: buildTreeEdges(base, parentMap).map((edge) => ({ ...edge, animated: false })),
    };
  }, [steps, connectors, agents.data, t]);

  return (
    <figure
      aria-label={t("diagram")}
      className="h-64 overflow-hidden rounded-lg border border-border-input bg-surface-base md:h-80"
    >
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          colorMode="system"
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </ReactFlowProvider>
    </figure>
  );
}
